import { Divider, Icons, Typography, Stack, tokens } from '@cast/design-system';

import { ENVIRONMENT_OPTIONS, PRIORITY_OPTIONS } from '../../_constants';
import { useAutoOptimization } from '../../_hooks/useAutoOptimization';
import { OptimizationGoal } from '../../open-api-types/autopilot-types';
import { WizardScreenLayout } from '../wizard/WizardScreenLayout';
import { IncludedLabel } from './_components/IncludedLabel';
import { SelectableCard } from './_components/SelectableCard';

const FEATURE_INFO_ITEMS = [
  {
    icon: <Icons.CastWorkloadAutoscaler size={16} variant="preset" />,
    name: 'Workload autoscaler',
    description:
      'Analyzes 7 days of usage and generates policies during the run — you review them after.',
  },
  {
    icon: <Icons.CastCostOptimization size={16} variant="preset" />,
    name: 'Node autoscaler',
    description:
      'Uses spot instances up to 90% of the time, picking the cheapest instance families.',
  },
  {
    icon: <Icons.PaintBrushHousehold size={16} />,
    name: 'Bin-packing',
    description:
      'Checks for consolidation opportunities every 5 minutes, packing pods tightly to free up nodes.',
  },
  {
    icon: <Icons.CastRebalancer size={16} />,
    name: 'Rebalancer',
    description:
      'Rebalances the cluster daily, consolidating aggressively to cut waste.',
  },
];

const PRIORITY_ICONS: Record<OptimizationGoal, React.ReactElement> = {
  COST_SAVING: <Icons.CurrencyDollar size={24} />,
  BALANCED: <Icons.Scales size={24} />,
  PERFORMANCE: <Icons.ShieldCheck size={24} />,
};

export const ProfileScreen = () => {
  const { state, onSelectPriority, onSelectEnvironment } =
    useAutoOptimization();

  return (
    <WizardScreenLayout
      title="Pick optimization profile"
      description="Select how aggressively Cast AI should optimize your cluster. Your optimization profile applies to both features and can be changed at any time."
    >
      <Stack>
        <Stack gap={24}>
          <Stack gap={8}>
            <Typography variant="A1">Choose optimization profile</Typography>

            <Stack direction="row" gap={16} sx={{ alignItems: 'flex-start' }}>
              {PRIORITY_OPTIONS.map((option) => (
                <SelectableCard
                  key={option.value}
                  value={option.value}
                  selected={state.preferences.priority === option.value}
                  onChange={onSelectPriority}
                  title={option.title}
                  description={option.description}
                  icon={PRIORITY_ICONS[option.value]}
                  testId={`priority-card-${option.value}`}
                />
              ))}
            </Stack>
          </Stack>

          <Stack gap={8}>
            <Typography variant="A1">How is this cluster used?</Typography>
            <Stack
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                alignItems: 'start',
              }}
            >
              {ENVIRONMENT_OPTIONS.map((option) => (
                <SelectableCard
                  key={option.value}
                  value={option.value}
                  selected={state.preferences.clusterUsage === option.value}
                  onChange={onSelectEnvironment}
                  title={option.title}
                  description={option.description}
                  testId={`environment-card-${option.value}`}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
        <Divider sx={{ my: 24 }} />
        <Stack gap={16}>
          <IncludedLabel text="How features will be tuned" />
          <Stack gap={12}>
            {FEATURE_INFO_ITEMS.map(({ icon, name, description }) => (
              <Stack key={name} direction="row" alignItems="center" gap={12}>
                {icon}
                <Stack direction="row" alignItems="baseline" gap={8}>
                  <Typography variant="P12B">{name}</Typography>
                  <Typography
                    variant="P12R"
                    sx={{ color: tokens.theme.fg.structural.secondary }}
                  >
                    {description}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </WizardScreenLayout>
  );
};
